import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import FootworkLogo from "@/components/FootworkLogo";
import { Delete, X } from "lucide-react";
import { sortUsersByRole } from "@/lib/users";

const ROLE_LABEL = {
  warehouse_manager: "Warehouse Mgr",
  operations_manager: "Operations Mgr",
  staff: "Staff",
};

const Login = () => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (user) nav("/");
  }, [user, nav]);

  useEffect(() => {
    api
      .get("/users/public")
      .then((r) => setUsers(sortUsersByRole(r.data)))
      .catch(() => toast.error("Could not load users"));
  }, []);

  const submit = async (fullPin) => {
    if (!selected || fullPin.length !== 6) return;
    setBusy(true);
    try {
      await login(selected.id, fullPin);
      toast.success(`Welcome ${selected.name}!`);
      nav("/");
    } catch (e) {
      // 423 Locked → account has 3 strikes and needs an admin reset;
      // 401 → still-live PIN just typed wrong. Different message +
      // different toast lifetime so the lockout is unmissable.
      const status = e?.response?.status;
      if (status === 423) {
        toast.error(
          `Account locked for ${selected.name} — ask your admin to reissue your PIN.`,
          { duration: 8000 },
        );
        // Kick the user back to the picker so they don't sit on a dead
        // PIN pad — visual signal that this account is off-limits.
        setSelected(null);
      } else {
        toast.error("Wrong PIN — try again");
      }
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  const press = (n) => {
    if (busy) return;
    // PINs are 6 digits — hardened from 4 in v124 for the cyber team.
    // Auto-submit on the 6th digit so staff don't have to press Enter.
    const next = (pin + n).slice(0, 6);
    setPin(next);
    if (next.length === 6) submit(next);
  };

  const back = () => setPin((p) => p.slice(0, -1));
  const clear = () => setPin("");

  return (
    <div
      className="min-h-screen flex items-stretch bg-[#FAFAF7]"
      data-testid="login-screen"
    >
      {/* LEFT: brand hero — black panel with footwork logo. Hidden on <md so
          the PIN pad + user grid can use the full screen width on phones. */}
      <div className="hidden md:flex md:w-1/2 bg-[#001740] text-white p-12 flex-col justify-between relative overflow-hidden">
        <div
          className="absolute -bottom-32 -left-24 opacity-[0.08] pointer-events-none"
          aria-hidden
        >
          <FootworkLogo className="h-[640px]" variant="mark" />
        </div>

        <div className="relative">
          <FootworkLogo className="h-14" variant="full" />
          <div className="mt-10">
            <div className="label-eyebrow text-[#228EBF]">stock control</div>
            <h1 className="font-display text-6xl leading-[0.95] mt-3 max-w-md">
              Custom orthotics, accurate inventory.
            </h1>
            <p className="text-white/70 mt-5 max-w-md text-lg leading-snug">
              Tap your name, enter your 6-digit PIN, and pick stock off the shelves.
              Every grab is logged with who, when and what.
            </p>
          </div>
        </div>

        <div className="relative grid grid-cols-3 gap-3 max-w-md">
          {[
            ["RUN", "click & go"],
            ["LOG", "every move tracked"],
            ["FLOW", "supplier to floor"],
          ].map(([k, v]) => (
            <div key={k} className="border-2 border-white/20 rounded-xl p-3">
              <div className="font-display text-2xl text-[#228EBF]">{k}</div>
              <div className="label-eyebrow text-white/60 mt-1">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT: user grid OR PIN pad */}
      <div className="w-full md:w-1/2 p-5 md:p-10 flex flex-col">
        {/* Mobile-only header with mini brand — replaces the hidden hero.
            Uses a dark chip so the white "footwork" wordmark is legible. */}
        <div className="md:hidden -mx-5 -mt-5 mb-5 px-5 py-3 bg-[#001740] flex items-center gap-3">
          <FootworkLogo className="h-8" variant="full" />
          <div className="label-eyebrow text-[#228EBF]">stock control</div>
        </div>
        {!selected ? (
          <>
            <div className="label-eyebrow">Step 1</div>
            <h2 className="font-display text-4xl mt-1 mb-6">Who&apos;s grabbing stock?</h2>
            <div
              className="grid grid-cols-2 sm:grid-cols-3 gap-4 overflow-y-auto scrollbar-clean pr-2"
              data-testid="user-grid"
            >
              {users.map((u) => {
                const locked = Boolean(u.locked_at);
                return (
                <button
                  key={u.id}
                  data-testid={`user-tile-${u.id}`}
                  disabled={locked}
                  onClick={() => {
                    if (locked) return;
                    setSelected(u);
                    setPin("");
                  }}
                  className={`nb-btn bg-white p-4 flex flex-col items-center gap-2 min-h-[150px] relative ${
                    locked ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {locked && (
                    <div
                      className="absolute top-2 right-2 bg-[#E5263F] text-white text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 rounded border-2 border-black"
                      data-testid={`user-tile-locked-${u.id}`}
                    >
                      Locked
                    </div>
                  )}
                  <div
                    className="w-16 h-16 rounded-full border-2 border-black font-display text-3xl flex items-center justify-center overflow-hidden"
                    style={{ background: u.color || "#228EBF", color: "#fff" }}
                    data-testid={`user-avatar-tile-${u.id}`}
                  >
                    {u.avatar_url ? (
                      // Public no-auth endpoint — 404s cleanly when the
                      // user has no photo, so the <img> hides itself and
                      // the coloured initials circle underneath stays
                      // visible. `onError` swaps the img out of the DOM
                      // so we never render the broken-image glyph.
                      <img
                        src={`${API}/users/${u.id}/avatar`}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      u.name?.[0]?.toUpperCase()
                    )}
                  </div>
                  <div className="font-display text-lg leading-tight text-center text-black">
                    {u.name}
                  </div>
                  <div className="label-eyebrow text-[#6B6B6B] text-center">
                    {locked ? (
                      <span className="text-[#8A1F1F] font-bold">
                        Ask admin to reset PIN
                      </span>
                    ) : (
                      <>
                        {ROLE_LABEL[u.role] || u.role}
                        {u.departments?.[0] && (
                          <span> · {u.departments[0]}</span>
                        )}
                      </>
                    )}
                  </div>
                </button>
                );
              })}
              {users.length === 0 && (
                <div className="col-span-2 sm:col-span-3 text-center text-[#6B6B6B] py-12">
                  No users yet — check backend seeding.
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-col h-full" data-testid="pin-screen">
            <button
              data-testid="pin-back-btn"
              onClick={() => setSelected(null)}
              className="self-start label-eyebrow flex items-center gap-1 hover:text-black"
            >
              <X size={14} /> change user
            </button>

            <div className="mt-4 mb-4 nb-card-sm p-4 flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center border-2 border-black font-display text-xl overflow-hidden"
                style={{ background: selected.color || "#228EBF", color: "#fff" }}
              >
                {selected.avatar_url ? (
                  <img
                    src={`${API}/users/${selected.id}/avatar`}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  selected.name?.[0]?.toUpperCase()
                )}
              </div>
              <div>
                <div className="font-display text-2xl">{selected.name}</div>
                <div className="label-eyebrow">{ROLE_LABEL[selected.role]}</div>
              </div>
            </div>

            <div className="label-eyebrow">Step 2 · Enter PIN</div>

            <div className="flex justify-center gap-2.5 my-5" data-testid="pin-dots">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`w-4 h-4 rounded-full border-2 border-black transition-all ${
                    pin.length > i ? "bg-[#228EBF] scale-110" : "bg-white"
                  }`}
                />
              ))}
            </div>

            <div className="grid grid-cols-3 gap-3 mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <button
                  key={n}
                  data-testid={`pin-key-${n}`}
                  onClick={() => press(String(n))}
                  className="nb-pin-key"
                >
                  {n}
                </button>
              ))}
              <button
                data-testid="pin-clear"
                onClick={clear}
                className="nb-pin-key"
                style={{ background: "#001740", color: "#fff" }}
              >
                C
              </button>
              <button
                data-testid="pin-key-0"
                onClick={() => press("0")}
                className="nb-pin-key"
              >
                0
              </button>
              <button
                data-testid="pin-back"
                onClick={back}
                className="nb-pin-key"
                style={{ background: "#001740", color: "#fff" }}
              >
                <Delete size={26} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
